'use client'

import { CreateOrganizationDialog } from '@/app/pages/Organizations/CreateOrganizationDialog'
import LogoMark from '@/components/ui/LogoMark'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useCreateOrgNavigation } from '@/hooks/useCreateOrgNavigation'
import { cn } from '@/lib/utils'
import type { PlatformUser } from '@/stores/auth'
import { useUser } from '@/stores/authStore'
import { useIsOwnerOfOrganization } from '@/utils/permissions'
import { ChevronsUpDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { OrganizationSwitcher } from './OrganizationSwitcher'
import { useOrganizations } from './useSwitcherData'

interface OrgSwitcherDropdownProps {
	currentOrgId: string | null
	currentOrgName?: string
	onOrganizationSelect: (orgId: string) => void
	onNavigate: () => void
	isActive?: boolean
}

export function OrgSwitcherDropdown({
	currentOrgId,
	currentOrgName,
	onOrganizationSelect,
	onNavigate,
	isActive,
}: OrgSwitcherDropdownProps) {
	const [open, setOpen] = useState(false)
	const user = useUser() as PlatformUser | null
	const isOwner = useIsOwnerOfOrganization(currentOrgId)

	const { showCreateDialog, setShowCreateDialog, handleOpenConsoleOrgCreateModal } =
		useCreateOrgNavigation(currentOrgId)

	const { organizations } = useOrganizations()

	const displayName =
		currentOrgName ||
		(currentOrgId ? organizations.find(o => o.id === currentOrgId)?.name : null) ||
		'Select Organization'

	const brandMark = useMemo(() => {
		if (!currentOrgId) {
			return null
		}
		const org = user?.organizations?.find(o => o.id === currentOrgId)
		return org?.frontend_settings?.branding?.brand_mark || null
	}, [user, currentOrgId])

	return (
		<>
			<Popover open={open} onOpenChange={setOpen}>
				<div
					className={cn(
						'group/tab flex items-center w-full rounded-md transition-all',
						open || isActive
							? 'bg-[var(--switcher-active-bg)] border border-[var(--switcher-active-border)] shadow-[var(--switcher-active-shadow)] text-sidebar-active-text'
							: 'text-sidebar-inactive-text hover:text-sidebar-active-text hover:bg-sidebar-accent/50 border border-transparent',
					)}>
					<button
						type='button'
						className={cn(
							'flex items-center gap-2 px-2 py-1.5 flex-1 min-w-0',
							isOwner ? 'cursor-pointer' : 'cursor-not-allowed opacity-70',
						)}
						onClick={() => {
							if (!isOwner) {
								toast.warning(
									'Only owners can access organization-wide sections. Contact an organization owner if you need this access.',
								)
								return
							}
							onNavigate()
						}}>
						<div className='size-8 rounded-lg overflow-hidden border border-border shrink-0 flex items-center justify-center bg-muted'>
							{brandMark ? (
								<img src={brandMark} className='size-full object-cover' alt='org icon' />
							) : (
								<LogoMark size='18px' />
							)}
						</div>
						<span
							className={cn(
								'truncate text-control font-bold uppercase tracking-wider flex-1 text-left',
								isActive ? 'text-sidebar-active-text' : 'text-sidebar-inactive-text',
							)}>
							{displayName}
						</span>
					</button>
					<PopoverTrigger asChild>
						<button
							type='button'
							className={cn(
								'shrink-0 flex items-center justify-center min-w-8 min-h-8 rounded-md cursor-pointer transition-opacity',
								open ? 'opacity-50' : 'opacity-0 group-hover/tab:opacity-100 hover:bg-sidebar-accent/80',
							)}>
							<ChevronsUpDown className='size-(--icon-standalone)' />
						</button>
					</PopoverTrigger>
				</div>
				<PopoverContent className='z-100 w-64 p-2 border-border' align='start'>
					<OrganizationSwitcher
						currentOrgId={currentOrgId}
						onOrganizationSelect={onOrganizationSelect}
						onSuccess={() => setOpen(false)}
						onCreateOrgClick={() => {
							setOpen(false)
							handleOpenConsoleOrgCreateModal()
						}}
					/>
				</PopoverContent>
			</Popover>
			<CreateOrganizationDialog open={showCreateDialog} onOpenChange={setShowCreateDialog} onSuccess={() => {}} />
		</>
	)
}
